import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Layers,
  Zap,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              La livraison de cartons pour les déménagements
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour vous éviter la perte de temps liée aux trajets dans
              les zones commerciales encombrées du 78, notre service de
              proximité gère toute la logistique de vos fournitures.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Acheminement direct à domicile",
                body: "Nous vous livrons vos cartons à plat directement devant votre porte à Croissy-sur-Seine, ainsi que dans les communes voisines comme Rueil-Malmaison, Le Vésinet, Chatou et Bougival.",
                Icon: MapPin,
              },
              {
                title: "Planification flexible",
                body: "Vous déterminez le créneau qui s'intègre le mieux dans votre emploi du temps, vous permettant de planifier sereinement la mise en boîte de vos pièces, pièce par pièce.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-8 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
          Grâce à cette réactivité à l&apos;échelle locale, votre
          meilleure alliée pour respecter votre calendrier de départ.
          En recevant des emballages propres, secs et prêts à
          l&apos;emploi sans bouger de chez vous, vous éliminez une
          source de stress majeure et pouvez vous concentrer pleinement
          sur le tri de vos affaires.
        </p>
      </div>
    </section>

    {/* ======= SECTION 3 — Matériaux robustes ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Matériaux pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des matériaux sélectionnés pour leur robustesse
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            La qualité de nos produits repose sur un cahier des charges
            strict, validé par les professionnels du secteur de la
            logistique.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Double cannelure */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Layers className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Double cannelure de qualité industrielle
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nos cartons offrent une résistance supérieure à la
              perforation, à la compression verticale et à
              l&apos;humidité par rapport aux emballages standards du
              commerce.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Anti-perforation
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Compression verticale renforcée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Résistance à l&apos;humidité
              </div>
            </div>
          </div>

          {/* Charges lourdes */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Boxes className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Protection des charges lourdes
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Qu&apos;il s&apos;agisse de transporter vos collections
              de livres, vos appareils technologiques ou vos objets
              d&apos;art, la structure rigide de nos produits garantit
              une barrière indéformable contre les pressions
              extérieures.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Idéal collections de livres
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                High-tech &amp; objets d&apos;art
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Barrière indéformable
              </div>
            </div>
          </div>
        </div>

        <p className="mt-8 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
          Une exigence de solidité vous apporte une barrière de
          sécurité indispensable contre les aléas de la route. En
          misant sur cette qualité industrielle, vous vous offrez
          l&apos;assurance que chaque carton conservera sa géométrie
          et ses propriétés de protection, du moment où vous le fermez
          jusqu&apos;à son ouverture dans votre nouveau lieu de vie.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-croissy-sur-seine-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos cartons pro
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-croissy-sur-seine-cta-kits"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Découvrir les kits
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Préparation rapide des commandes ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Réactivité totale
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Une préparation rapide de vos commandes
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Dans l&apos;effervescence d&apos;un changement de
              domicile, chaque heure compte et l&apos;attente n&apos;a
              pas sa place au sein de votre organisation. Dès la
              validation de vos achats sur notre boutique en ligne, nos
              équipes préparent immédiatement vos lots de cartons, kits
              et accessoires pour qu&apos;ils soient prêts à être
              expédiés sans le moindre délai.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour garantir cette réactivité absolue, nous maintenons
              un inventaire permanent de toutes nos références
              professionnelles, ce qui nous permet d&apos;éviter les
              ruptures de stock et de traiter les demandes urgentes
              instantanément.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Cette efficacité opérationnelle vous permet de réagir au
              plus vite, même si vous vous apercevez au dernier moment
              qu&apos;il vous manque quelques cartons pour boucler une
              pièce. En éliminant les délais d&apos;attente habituels,
              nous vous offrons la possibilité de maintenir votre
              planning de préparation parfaitement fluide et
              d&apos;avancer dans vos colis sans subir de temps mort.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-croissy-sur-seine-cta-commande"
              >
                <Zap className="h-4 w-4" strokeWidth={2.6} />
                Commander maintenant
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/livraison"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-croissy-sur-seine-cta-livraison"
              >
                <Truck className="h-4 w-4" strokeWidth={2.6} />
                Nos modalités de livraison
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Zap className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  Promesse de réactivité
                </h3>
              </div>
              <div className="space-y-2.5 text-[14px] text-[#3a4856]">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Préparation immédiate
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Inventaire permanent
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Zéro rupture de stock
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Demandes urgentes traitées
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Planning sans temps mort
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-croissy-sur-seine-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
